import {
  customAlertApiRef,
  GenericEntityType,
  getLabelAndValue,
  KindEnum,
  NotFoundException,
  parseDisplayField,
  PreviewMessage,
  selectTemplateEntity,
  TemplateEntity,
  TextFieldValue,
  useEnvironmentsContext,
  useLoaderNunjucks,
  WbGeneralPurposeEntityPicker,
  wbSafeNunjucksApiRef,
} from '@agilelab/plugin-wb-platform';
import { witboostSearchApiRef } from '@agilelab/plugin-wb-search';
import { knowledgeGraphManagerApiRef } from './knowledgeGraphManagerApiRef';

import { identityApiRef, useApi } from '@mainstage/core-plugin-api';
import { catalogApiRef } from '@mainstage/plugin-catalog-react';
import { FormControl, FormHelperText } from '@material-ui/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLatest } from 'react-use';
import { extractCustomProperties, isHidden } from '../../utils';
import {
  AvailableEntities,
  entityHandler,
} from './providers/entityProvider.factory';
import { areArraysEqual } from '@agilelab/plugin-wb-platform-common';

import { EntitySearchPickerProps } from './schema';
import { WittySuggestFieldRequest } from '@agilelab/plugin-wb-witty-common';
import {
  wittyAutocompleteApiRef,
  useWittyTable,
  findTableRowIndexOfField,
} from '@agilelab/plugin-wb-witty-react';
import { UseDependencyManagerResult } from '../../MultiStepJsonForm/DependencyManager';
import { extractFieldPathFromRjsfId } from '../../MultiStepJsonForm/utils/extractFieldPathFromRjsfId';

function isRecord(value: any): value is Record<string, any> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

export interface EntitySearchPickerUiOptions {
  multiSelection?: boolean;
  entities?: TemplateEntity[];
  allowArbitraryValues?: boolean;
  'ui:widget'?: string;
}

type FormDataType = string | GenericEntityType | (string | GenericEntityType)[];

export const EntitySearchPicker = (props: EntitySearchPickerProps) => {
  const {
    formData,
    schema: { title = 'Entity', description },
    required,
    uiSchema,
    rawErrors,
    idSchema,
    formContext,
  } = props;

  const onChangeRef = useLatest(props.onChange);
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const formContextRef = useLatest(formContext);

  // Extract dependency manager and field path
  const dependencyManager = formContext?.dependencyManager as
    | UseDependencyManagerResult
    | undefined;
  const rootSchema = formContext?.rootSchema;
  const fieldPath = useMemo(() => {
    const id = idSchema?.$id || '';
    return extractFieldPathFromRjsfId(id, rootSchema);
  }, [idSchema, rootSchema]);

  const searchApi = useApi(witboostSearchApiRef);
  const alertApi = useApi(customAlertApiRef);
  const nunjucksApi = useApi(wbSafeNunjucksApiRef);
  const kgmApi = useApi(knowledgeGraphManagerApiRef);
  const { environmentList, priorityEnvironment } = useEnvironmentsContext();

  const [templateError, setTemplateError] = useState<PreviewMessage>();
  const [currentValue, setCurrentValue] = useState<TextFieldValue[]>([]);
  const currentValueRef = useLatest(currentValue);
  const [resolvedValues, setResolvedValues] = useState<
    TextFieldValue[] | undefined
  >(undefined);

  const multiSelection = uiSchema['ui:options']?.multiSelection ?? false;
  const disabled = uiSchema['ui:disabled'] ?? false;

  const customProps = extractCustomProperties(uiSchema);

  const availableKinds = useMemo(() => {
    const kinds = uiSchema['ui:options']?.entities ?? [];
    return kinds.filter(k =>
      Object.values(AvailableEntities).includes(k.type as AvailableEntities),
    );
  }, [uiSchema]);

  const { isReady: displayFieldsReady } = useLoaderNunjucks(
    availableKinds.flatMap(kind =>
      kind.displayField ? [kind.displayField] : [],
    ),
  );

  const providerInitResult = useMemo<{
    providerMapper: ReturnType<typeof entityHandler>;
    providerInitError?: string;
  }>(() => {
    try {
      return {
        providerMapper: entityHandler(
          searchApi,
          catalogApi,
          nunjucksApi,
          availableKinds,
          environmentList,
          priorityEnvironment,
          formContext,
          kgmApi,
        ),
      };
    } catch (error) {
      return {
        providerMapper: new Map(),
        providerInitError:
          error instanceof Error
            ? error.message
            : 'Error initializing entity providers.',
      };
    }
  }, [
    availableKinds,
    catalogApi,
    environmentList,
    priorityEnvironment,
    searchApi,
    nunjucksApi,
    formContext,
    kgmApi,
  ]);
  const { providerMapper } = providerInitResult;

  // Providers may flag their own configuration as invalid (e.g. an
  // unsupported returnField) instead of throwing, so the field can be
  // disabled with an inline error rather than crashing the whole form.
  const providerConfigError = useMemo(() => {
    for (const provider of providerMapper.values()) {
      const configError = (provider as { configError?: string }).configError;
      if (configError) {
        return configError;
      }
    }
    return undefined;
  }, [providerMapper]);

  const fieldError =
    providerInitResult.providerInitError ?? providerConfigError;

  const wittyApi = useApi(wittyAutocompleteApiRef);
  const wittyTable = useWittyTable();

  const fetchAIData = useCallback(
    async (data: GenericEntityType[]): Promise<GenericEntityType[]> => {
      try {
        if (!wittyTable) {
          throw new Error(
            `You are using a Witty enabled picker outside of a Witty table. Please adjust the template`,
          );
        }

        const credentials = await identityApi.getCredentials();
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };
        if (credentials.token) {
          headers.Authorization = `Bearer ${credentials.token}`;
        }
        const fieldId = idSchema.$id;
        const suggestFields: WittySuggestFieldRequest[] = [
          {
            type: 'multiselect' as const,
            path: fieldId,
            optionsSet: fieldId,
          },
        ];

        const idToData = new Map<string, GenericEntityType>();
        const options: any[] = [];
        for (const singleData of data) {
          options.push({
            id: (singleData as any).id,
            name: (singleData as any).name || '',
            description: (singleData as any).description || '',
          });
          idToData.set((singleData as any).id, singleData);
        }

        const rowIndex = findTableRowIndexOfField(
          wittyTable.tablePath,
          fieldId,
        );

        const response = await wittyApi.autocomplete({
          tablePath: wittyTable.tablePath,
          formData: formContextRef.current,
          suggestFields,
          selectRowIndex: rowIndex,
          optionsSets: {
            [fieldId]: options,
          },
          additionalContext: wittyTable?.additionalContext ?? [],
        });
        let selectedOptions: GenericEntityType[] = [];

        if (Array.isArray(response.suggestions[fieldId])) {
          selectedOptions = (response.suggestions[fieldId] as any[])
            .map((s: any) => idToData.get(s.id))
            .filter(Boolean) as GenericEntityType[];
        }

        return selectedOptions;
      } catch (error) {
        throw new Error(
          `Error fetching AI data: ${
            error instanceof Error ? error.message : error
          }`,
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      identityApi,
      idSchema?.$id,
      wittyApi,
      wittyTable?.tablePath,
      wittyTable?.additionalContext,
    ],
  );

  useEffect(() => {
    if (!availableKinds || availableKinds.length === 0) {
      setTemplateError({
        title: 'Error',
        type: 'error',
        content:
          'Error while fetching entities from the template. Please contact the platform team.',
      });
    }
  }, [availableKinds]);

  const fetchData = useCallback(
    async (ref: string) => {
      for (const [key, provider] of Array.from(providerMapper.entries())) {
        if (availableKinds.some(a => a.type === key)) {
          try {
            const res = await provider.resolve(ref);
            if (res) {
              return res;
            }
          } catch (err) {
            if (err instanceof NotFoundException) {
              return undefined;
            }
            alertApi.post({
              error: err,
              severity: 'error',
            });
            return undefined;
          }
        }
      }
      return undefined;
    },
    [alertApi, availableKinds, providerMapper],
  );

  const resolveEntitiesByFormData = useCallback(
    async (data: FormDataType): Promise<TextFieldValue[]> => {
      const checkCurrentValue = (value: string) => {
        const justResolved = currentValueRef.current?.find(
          cv => cv.value === value,
        );
        return justResolved ? justResolved : null;
      };
      const recursiveResolve = async (
        input: FormDataType,
      ): Promise<TextFieldValue[]> => {
        if (Array.isArray(input)) {
          const results = await Promise.allSettled(
            input.map(item => recursiveResolve(item)),
          );
          return results
            .filter(result => result.status === 'fulfilled')
            .flatMap(result => (result as PromiseFulfilledResult<any[]>).value);
        }
        if (isRecord(input)) {
          const justResolved = checkCurrentValue(input.value);
          if (justResolved) return [justResolved];

          const templateEntity = selectTemplateEntity(
            availableKinds,
            input.__metadata.kind,
          );
          const renderedLabel = parseDisplayField(
            nunjucksApi,
            input,
            templateEntity?.displayField,
          );
          const label = renderedLabel?.trim()
            ? renderedLabel
            : input.__metadata.name;

          const adaptedObj = {
            kind: input.__metadata.kind,
            name: input.__metadata.name,
            urn:
              (typeof input.urn === 'string' && input.urn) ||
              (typeof input.value === 'string' && input.value) ||
              (typeof input.id === 'string' && input.id) ||
              '',
            ref:
              (typeof input.ref === 'string' && input.ref) ||
              (typeof input.value === 'string' && input.value) ||
              (typeof input.id === 'string' && input.id) ||
              '',
            ...input,
            label,
            // Preserve tree path for entities with ancestry information
            // (e.g. RDF concepts store it as __treePath on the entity)
            ...(Array.isArray(input.__treePath) && input.__treePath.length > 0
              ? {
                  treePath: [...input.__treePath, label],
                }
              : {}),
          };

          const entity = getLabelAndValue(input, adaptedObj, templateEntity);

          // __treePath is stripped when the selection is persisted to form
          // data (stripInternalEntityProps removes all `__`-prefixed keys
          // except `__metadata`), so hierarchical entities restored from a
          // full/fieldsToSave record lose their ancestor path on reload.
          // Ask the provider to recompute it so the hierarchy panel doesn't
          // show the item as a top-level concept.
          if (!entity.treePath && adaptedObj.ref) {
            const provider = providerMapper.get(templateEntity?.type ?? '');
            if (provider?.fetchAncestors) {
              try {
                const treePath = await provider.fetchAncestors(adaptedObj.ref);
                if (treePath && treePath.length > 0) {
                  entity.treePath = treePath;
                }
              } catch {
                // Best-effort restore — fall back to showing the item
                // without hierarchy rather than failing resolution.
              }
            }
          }

          return [entity];
        }
        if (typeof input === 'string') {
          const justResolved = checkCurrentValue(input);
          if (justResolved) {
            return [justResolved];
          }

          const entity = await fetchData(input);

          if (entity) return [entity];
        }
        return [
          {
            kind: KindEnum.arbitrary,
            label: input,
            value: input,
          },
        ];
      };
      return recursiveResolve(data);
    },
    [currentValueRef, availableKinds, fetchData, providerMapper, nunjucksApi],
  );

  const hasResolved = useRef(false);
  useEffect(() => {
    if (hasResolved.current || !displayFieldsReady) return;
    if (rawErrors?.length) {
      return;
    }
    const resolveEntities = async () => {
      const formDataAvailable =
        formData &&
        (props.schema.type === 'object'
          ? Object.keys(formData).length
          : formData);
      if (formDataAvailable) {
        hasResolved.current = true;
        const resolvedEntities = await resolveEntitiesByFormData(formData);
        setResolvedValues(resolvedEntities);

        // When the picker is hydrated from prefilled formData (for example when
        // opening a template from a system page "Add" action), the visible
        // picker value is restored here without going through onSelect.
        // Downstream hidden dependents still need one dependency-manager kick
        // to populate their derived values (e.g. dataproduct -> object -> urn
        // -> object chains), otherwise fields used later in the template remain
        // unset even though the source picker shows a value.
        const hasHydratedValues = resolvedEntities.length > 0;

        // Notify exactly once for the mount-time hydration path. This mirrors
        // the dependency propagation that normally happens in onSelect. The
        // hasResolved guard above remains the loop-prevention boundary.
        if (hasHydratedValues && dependencyManager?.notifyUpdate && fieldPath) {
          dependencyManager.notifyUpdate({
            fieldPath,
            value: formData,
            source: 'system',
            intent: 'manual',
          });
        }
      }
    };
    resolveEntities();
  }, [
    formData,
    resolveEntitiesByFormData,
    props.schema.type,
    rawErrors,
    dependencyManager,
    fieldPath,
    currentValueRef,
    displayFieldsReady,
  ]);

  const onSelect = useCallback(
    (values: TextFieldValue[] | undefined) => {
      if (!values && currentValue.length === 0) return;
      if (values && areArraysEqual(values, currentValue)) return;
      setCurrentValue(values ?? []);
      const formDataValues = values?.flatMap(v => (v?.value ? [v.value] : []));
      const newValue = multiSelection ? formDataValues : formDataValues?.[0];

      // Notify dependency manager to propagate changes to dependents, or fallback to onChange
      if (dependencyManager?.notifyUpdate && fieldPath) {
        dependencyManager.notifyUpdate({
          fieldPath,
          value: newValue,
          source: 'user',
          intent: 'manual',
        });
      } else {
        // Fallback: if no dependency manager, call onChange directly
        onChangeRef.current(newValue);
      }
    },
    [currentValue, multiSelection, onChangeRef, dependencyManager, fieldPath],
  );

  return (
    <FormControl
      style={{ display: isHidden(uiSchema) ? 'none' : undefined }}
      required={required}
      error={(rawErrors?.length > 0 && !currentValue) || !!fieldError}
    >
      <WbGeneralPurposeEntityPicker
        title={title}
        description={description}
        required={required ?? false}
        id={idSchema?.$id}
        resolvedValues={resolvedValues}
        value={currentValue ?? []}
        disabled={disabled || !!fieldError}
        onSelect={onSelect}
        freeSolo={
          (uiSchema['ui:options']?.allowArbitraryValues as boolean) ?? false
        }
        multiSelection={multiSelection}
        availableKinds={availableKinds}
        providerMapper={providerMapper}
        parentTemplateRef={formContext.templateRef}
        templateError={templateError}
        fetchAiData={fetchAIData}
        formContext={formContextRef.current}
        {...customProps}
      />
      {fieldError && (
        <FormHelperText id={`${idSchema?.$id}-error-text`}>
          {`This field has been disabled due to a misconfiguration error. ${fieldError} Please contact the platform team to address this issue.`}
        </FormHelperText>
      )}
    </FormControl>
  );
};
